import React, { FC, useState, useEffect, useContext } from 'react';
import TokenManagerContext from '../../core/auth/TokenManager';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  Chip,
  CircularProgress,
  Tooltip,
  Divider,
  makeStyles,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Edit as EditIcon,
  MusicNote as MusicNoteIcon,
  LibraryMusic as LibraryMusicIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Image as ImageIcon,
} from '@material-ui/icons';
import { useQuery, useMutation } from '@apollo/client';
import GET_NEW_SECTION, {
  GetNewSectionResponse,
  NewSectionItem,
  NewSectionSong,
} from '../../core/graphql/queries/getNewSection';
import {
  CREATE_NEW_SECTION_ITEM,
  UPDATE_NEW_SECTION_ITEM,
  DELETE_NEW_SECTION_ITEM,
  REORDER_NEW_SECTION_ITEMS,
  CREATE_NEW_SECTION_SONG,
  DELETE_NEW_SECTION_SONG,
  REORDER_NEW_SECTION_SONGS,
  ADMIN_SEARCH,
} from '../../core/graphql/mutations/newSection';

const PLACEHOLDER = 'https://via.placeholder.com/48x48/02172a/fff?text=?';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#02172a',
    minHeight: '100vh',
    color: '#fff',
  },
  pageTitle: { color: '#fff', fontWeight: 700, marginBottom: theme.spacing(0.5) },
  pageSubtitle: {
    color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', marginBottom: theme.spacing(3),
  },
  section: {
    backgroundColor: '#10354D', borderRadius: 12,
    padding: theme.spacing(3), marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    color: '#fff', fontWeight: 600, fontSize: '1.1rem',
    display: 'flex', alignItems: 'center', gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  sectionIcon: { color: '#5dc4b2' },
  addButton: {
    backgroundColor: '#5dc4b2', color: '#02172a', fontWeight: 600,
    textTransform: 'none', borderRadius: 20, padding: theme.spacing(0.75, 2),
    '&:hover': { backgroundColor: '#4ab0a0' },
  },
  tableHead: {
    '& .MuiTableCell-head': {
      backgroundColor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.6)',
      fontSize: '0.75rem', fontWeight: 600, letterSpacing: 0.8,
      textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.08)',
    },
  },
  tableRow: {
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' },
    '& .MuiTableCell-body': {
      borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#fff',
      padding: theme.spacing(1.5, 2),
    },
  },
  thumbnail: {
    width: 56, height: 32, borderRadius: 4,
    objectFit: 'cover' as const, backgroundColor: '#02172a',
  },
  chip: {
    fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.8, height: 22,
    backgroundColor: 'rgba(93,196,178,0.15)', color: '#5dc4b2',
    border: '1px solid rgba(93,196,178,0.3)',
  },
  orderBtn: {
    padding: 2, color: 'rgba(255,255,255,0.4)',
    '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
    '&:disabled': { color: 'rgba(255,255,255,0.15)' },
  },
  editBtn: { color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#5dc4b2' } },
  deleteBtn: { color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#f44336' } },
  emptyRow: {
    textAlign: 'center' as const, color: 'rgba(255,255,255,0.3)',
    padding: theme.spacing(4), fontStyle: 'italic',
  },
  dialog: {
    '& .MuiPaper-root': {
      backgroundColor: '#0a2540', color: '#fff', borderRadius: 12, minWidth: 520,
      maxWidth: 580,
    },
  },
  dialogTitle: {
    color: '#fff', fontWeight: 600,
    borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: theme.spacing(2),
  },
  field: {
    '& .MuiOutlinedInput-root': {
      color: '#fff',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
      '&.Mui-focused fieldset': { borderColor: '#5dc4b2' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#5dc4b2' },
  },
  imagePreview: {
    width: '100%', aspectRatio: '9/5', objectFit: 'cover' as const,
    borderRadius: 8, backgroundColor: '#10354D', display: 'block',
  },
  imagePlaceholder: {
    width: '100%', aspectRatio: '9/5', borderRadius: 8,
    backgroundColor: '#10354D', display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexDirection: 'column' as const,
    color: 'rgba(255,255,255,0.2)', border: '2px dashed rgba(255,255,255,0.12)',
    cursor: 'pointer',
    '&:hover': { borderColor: '#5dc4b2', color: '#5dc4b2' },
  },
  uploadBtn: {
    borderColor: 'rgba(255,255,255,0.25)', color: '#fff', textTransform: 'none' as const,
    '&:hover': { borderColor: '#5dc4b2', backgroundColor: 'rgba(93,196,178,0.08)' },
    '&:disabled': { borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' },
  },
  uploadSuccess: {
    color: '#5dc4b2', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4,
  },
  changeImageOverlay: {
    position: 'absolute' as const, inset: 0, display: 'flex', alignItems: 'center',
    justifyContent: 'center', borderRadius: 8,
    backgroundColor: 'rgba(0,0,0,0)', transition: 'background-color 0.2s',
    cursor: 'pointer',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.55)' },
    '&:hover $changeImageLabel': { opacity: 1 },
  },
  changeImageLabel: {
    opacity: 0, transition: 'opacity 0.2s', display: 'flex', flexDirection: 'column' as const,
    alignItems: 'center', gap: 4, color: '#fff',
  },
  sectionDivider: {
    backgroundColor: 'rgba(255,255,255,0.08)', margin: theme.spacing(2, 0),
  },
  sectionLabel: {
    fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const,
    color: 'rgba(255,255,255,0.35)', marginBottom: theme.spacing(1.5),
  },
  searchResult: {
    display: 'flex', alignItems: 'center', gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5), borderRadius: 8, cursor: 'pointer',
    '&:hover': { backgroundColor: 'rgba(93,196,178,0.12)' },
  },
  searchResultThumb: {
    width: 40, height: 40, borderRadius: 4,
    objectFit: 'cover' as const, backgroundColor: '#10354D', flexShrink: 0,
  },
  selectedResult: {
    backgroundColor: 'rgba(93,196,178,0.18)', border: '1px solid rgba(93,196,178,0.5)',
  },
  confirmBtn: {
    backgroundColor: '#5dc4b2', color: '#02172a', fontWeight: 600, textTransform: 'none' as const,
    '&:hover': { backgroundColor: '#4ab0a0' },
    '&:disabled': { backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' },
  },
  cancelBtn: { color: 'rgba(255,255,255,0.6)', textTransform: 'none' as const },
  descPreview: {
    fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
    maxWidth: 220,
  },
}));

// ── Shared helper for optional album link search ──────────────────────────────
interface AlbumLinkSearchProps {
  classes: ReturnType<typeof useStyles>;
  linkedAlbum: any;
  onSelect: (album: any) => void;
  onClear: () => void;
}
const AlbumLinkSearch: FC<AlbumLinkSearchProps> = ({ classes, linkedAlbum, onSelect, onClear }) => {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 400);
    return () => clearTimeout(t);
  }, [query]);

  const { data, loading } = useQuery(ADMIN_SEARCH, {
    variables: { term: debounced },
    skip: debounced.trim().length < 2,
    fetchPolicy: 'network-only',
  });

  const results: any[] = (data?.searchResponse || []).filter(
    (r: any) => r.responseType === 'ALBUM',
  );

  if (linkedAlbum) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: 10 }}>
        <img src={linkedAlbum.image || linkedAlbum.imageUrl || PLACEHOLDER} alt={linkedAlbum.name || linkedAlbum.title} className={classes.searchResultThumb} />
        <Box flex={1} minWidth={0}>
          <Typography style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 500 }}>
            {linkedAlbum.name || linkedAlbum.title}
          </Typography>
          <Typography style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem' }}>
            Linked for click-through
          </Typography>
        </Box>
        <Button size="small" onClick={onClear} className={classes.cancelBtn}>Change</Button>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
      <TextField
        label="Search albums to link (optional)" variant="outlined" size="small" fullWidth
        value={query} onChange={(e) => setQuery(e.target.value)}
        className={classes.field} placeholder="Type to search..."
      />
      {loading && (
        <Box display="flex" justifyContent="center" py={1}>
          <CircularProgress size={18} style={{ color: '#5dc4b2' }} />
        </Box>
      )}
      {results.length > 0 && (
        <Box style={{ maxHeight: 160, overflowY: 'auto' }}>
          {results.map((album: any) => (
            <Box key={album.id} className={classes.searchResult} onClick={() => onSelect(album)}>
              <img src={album.image || PLACEHOLDER} alt={album.name} className={classes.searchResultThumb} />
              <Typography style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 500 }}>
                {album.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ── Item form fields shared by Add and Edit ───────────────────────────────────
interface ItemFormState {
  label: string;
  title: string;
  subtitle: string;
  description: string;
  customImageUrl: string;
  linkedAlbum: any;
}
const emptyForm = (): ItemFormState => ({
  label: '', title: '', subtitle: '', description: '', customImageUrl: '', linkedAlbum: null,
});

interface ItemDialogProps {
  open: boolean;
  initialValues?: Partial<ItemFormState>;
  dialogTitle: string;
  confirmLabel: string;
  onClose: () => void;
  onSave: (values: ItemFormState) => Promise<void>;
  disabled?: boolean;
}
const ItemDialog: FC<ItemDialogProps> = ({
  open, initialValues, dialogTitle, confirmLabel, onClose, onSave, disabled,
}) => {
  const classes = useStyles();
  const tokenManager = useContext(TokenManagerContext);
  const [form, setForm] = useState<ItemFormState>(emptyForm());
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Reset form whenever the dialog opens
  useEffect(() => {
    if (open) {
      setForm({ ...emptyForm(), ...(initialValues || {}) });
      setUploadError('');
      setSaveError('');
      setSaving(false);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key: keyof ItemFormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError('');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // POST through the backend server — no S3 CORS config needed
      const { data } = await tokenManager.axiosClient.post(
        '/admin/hero-image/upload',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      setForm((f) => ({ ...f, customImageUrl: data.url }));
    } catch (err: any) {
      const msg = err?.response?.data?.error || err.message || 'Upload failed. Please try again.';
      setUploadError(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Only title and artwork are required; label auto-fills from title if left blank
  const isValid = form.title.trim() && form.customImageUrl.trim();

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog}>
      <DialogTitle className={classes.dialogTitle}>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Box mt={2} display="flex" flexDirection="column" style={{ gap: 16 }}>

          {/* ── Artwork area ───────────────────────────────────────────────── */}
          {/* Hidden native file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          {form.customImageUrl ? (
            /* Show the uploaded image with a hover-to-change overlay */
            <Box style={{ position: 'relative' }}>
              <img
                src={form.customImageUrl}
                alt="Artwork preview"
                className={classes.imagePreview}
                onError={(e) => { (e.target as HTMLImageElement).src = ''; }}
              />
              <Box
                className={classes.changeImageOverlay}
                onClick={() => !uploading && fileInputRef.current?.click()}
              >
                <Box className={classes.changeImageLabel}>
                  <CloudUploadIcon style={{ fontSize: 28 }} />
                  <Typography style={{ fontSize: '0.78rem', fontWeight: 600 }}>
                    {uploading ? 'Uploading…' : 'Change image'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            /* Empty state — clicking the whole area opens the file picker */
            <Box
              className={classes.imagePlaceholder}
              onClick={() => !uploading && fileInputRef.current?.click()}
            >
              {uploading ? (
                <CircularProgress size={28} style={{ color: '#5dc4b2', marginBottom: 8 }} />
              ) : (
                <CloudUploadIcon style={{ fontSize: 32, marginBottom: 6 }} />
              )}
              <Typography style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                {uploading ? 'Uploading…' : 'Click to upload artwork'}
              </Typography>
              <Typography style={{ fontSize: '0.68rem', marginTop: 4 }}>
                Recommended: 1440 × 800 px · 9:5 ratio
              </Typography>
            </Box>
          )}

          {/* Upload status row */}
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                uploading
                  ? <CircularProgress size={14} style={{ color: 'inherit' }} />
                  : <CloudUploadIcon style={{ fontSize: 16 }} />
              }
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className={classes.uploadBtn}
            >
              {uploading ? 'Uploading…' : form.customImageUrl ? 'Replace image' : 'Upload image'}
            </Button>

            {form.customImageUrl && !uploading && (
              <Box className={classes.uploadSuccess}>
                <CheckCircleIcon style={{ fontSize: 16 }} />
                Image ready
              </Box>
            )}

            {uploadError && (
              <Typography style={{ color: '#f44336', fontSize: '0.75rem' }}>
                {uploadError}
              </Typography>
            )}
          </Box>

          <Divider className={classes.sectionDivider} />
          <Typography className={classes.sectionLabel}>Text above the artwork</Typography>

          <TextField
            label="Type label" variant="outlined" size="small" fullWidth
            value={form.label} onChange={set('label')}
            className={classes.field}
            placeholder="e.g. NEW ALBUM, UPDATED PLAYLIST"
            helperText={<span style={{ color: 'rgba(255,255,255,0.35)' }}>Optional — small badge shown above the title. Defaults to the title if blank.</span>}
          />

          <TextField
            label="Title *" variant="outlined" size="small" fullWidth
            value={form.title} onChange={set('title')}
            className={classes.field} placeholder="e.g. BROWN"
            error={!form.title.trim()}
            helperText={!form.title.trim() ? <span style={{ color: '#f44336' }}>Required</span> : undefined}
          />

          <TextField
            label="Subtitle / Artist" variant="outlined" size="small" fullWidth
            value={form.subtitle} onChange={set('subtitle')}
            className={classes.field} placeholder="e.g. Chris Brown"
          />

          <Divider className={classes.sectionDivider} />
          <Typography className={classes.sectionLabel}>Text overlaid on the artwork (bottom)</Typography>

          <TextField
            label="Description" variant="outlined" size="small" fullWidth multiline rows={3}
            value={form.description} onChange={set('description')}
            className={classes.field}
            placeholder="e.g. Charli xcx is making 'Rock Music' great again…"
            helperText={<span style={{ color: 'rgba(255,255,255,0.35)' }}>Displayed over a gradient at the bottom of the image. Up to ~3 lines.</span>}
          />

          <Divider className={classes.sectionDivider} />
          <Typography className={classes.sectionLabel}>Album link (optional — for click-through)</Typography>

          <AlbumLinkSearch
            classes={classes}
            linkedAlbum={form.linkedAlbum}
            onSelect={(album) => setForm((f) => ({ ...f, linkedAlbum: album }))}
            onClear={() => setForm((f) => ({ ...f, linkedAlbum: null }))}
          />
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.1)', flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
        {saveError && (
          <Typography style={{ color: '#f44336', fontSize: '0.8rem', textAlign: 'center', padding: '0 4px' }}>
            {saveError}
          </Typography>
        )}
        <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
          <Button onClick={onClose} className={classes.cancelBtn} disabled={saving}>Cancel</Button>
          <Box style={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            {!isValid && !saving && (
              <Typography style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>
                {!form.customImageUrl.trim() ? 'Upload an image to continue' : 'Fill in the Title to continue'}
              </Typography>
            )}
            <Button
              onClick={async () => {
                if (!isValid) return;
                setSaving(true);
                setSaveError('');
                try {
                  await onSave(form);
                  onClose();
                } catch (err: any) {
                  const msg = err?.graphQLErrors?.[0]?.message || err?.message || 'Save failed. Please try again.';
                  setSaveError(msg);
                } finally {
                  setSaving(false);
                }
              }}
              disabled={!isValid || disabled || uploading || saving}
              className={classes.confirmBtn}
              variant="contained"
            >
              {saving ? <CircularProgress size={18} style={{ color: '#02172a' }} /> : confirmLabel}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// ── Add Song Dialog ───────────────────────────────────────────────────────────
interface AddSongDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (songId: string) => void;
}
const AddSongDialog: FC<AddSongDialogProps> = ({ open, onClose, onAdd }) => {
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedSong, setSelectedSong] = useState<any>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data, loading } = useQuery(ADMIN_SEARCH, {
    variables: { term: debouncedQuery },
    skip: debouncedQuery.trim().length < 2,
    fetchPolicy: 'network-only',
  });

  const songResults: any[] = (data?.searchResponse || []).filter(
    (r: any) => r.responseType === 'SONG',
  );

  const handleClose = () => {
    setSearchQuery('');
    setSelectedSong(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} className={classes.dialog}>
      <DialogTitle className={classes.dialogTitle}>Add to Best New Songs</DialogTitle>
      <DialogContent>
        <Box mt={2} display="flex" flexDirection="column" style={{ gap: 16 }}>
          <TextField
            label="Search Songs" variant="outlined" size="small" fullWidth autoFocus
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setSelectedSong(null); }}
            className={classes.field} placeholder="Type at least 2 characters..."
          />
          {loading && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={20} style={{ color: '#5dc4b2' }} />
            </Box>
          )}
          {songResults.length > 0 && (
            <Box style={{ maxHeight: 220, overflowY: 'auto' }}>
              {songResults.map((song: any) => (
                <Box
                  key={song.id}
                  className={`${classes.searchResult} ${selectedSong?.id === song.id ? classes.selectedResult : ''}`}
                  onClick={() => setSelectedSong(song)}
                >
                  <img src={song.image || PLACEHOLDER} alt={song.name || 'Song'} className={classes.searchResultThumb} />
                  <Box>
                    <Typography style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 500 }}>
                      {song.name || 'Untitled'}
                    </Typography>
                    <Typography style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem' }}>
                      {song.plays} plays
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {debouncedQuery.trim().length >= 2 && !loading && songResults.length === 0 && (
            <Typography style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', textAlign: 'center' }}>
              No songs found
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Button onClick={handleClose} className={classes.cancelBtn}>Cancel</Button>
        <Button
          onClick={() => { if (selectedSong) { onAdd(selectedSong.id); handleClose(); } }}
          disabled={!selectedSong}
          className={classes.confirmBtn}
          variant="contained"
        >
          Add Song
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const HomeHeroes: FC = () => {
  const classes = useStyles();
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [editItem, setEditItem] = useState<NewSectionItem | null>(null);
  const [addSongOpen, setAddSongOpen] = useState(false);

  const { data, loading, refetch } = useQuery<GetNewSectionResponse>(GET_NEW_SECTION, {
    fetchPolicy: 'network-only',
  });

  const [createItem] = useMutation(CREATE_NEW_SECTION_ITEM);
  const [updateItem] = useMutation(UPDATE_NEW_SECTION_ITEM);
  const [deleteItem] = useMutation(DELETE_NEW_SECTION_ITEM);
  const [reorderItems] = useMutation(REORDER_NEW_SECTION_ITEMS);
  const [createSong] = useMutation(CREATE_NEW_SECTION_SONG);
  const [deleteSong] = useMutation(DELETE_NEW_SECTION_SONG);
  const [reorderSongs] = useMutation(REORDER_NEW_SECTION_SONGS);

  const items: NewSectionItem[] = [...(data?.newSection?.items || [])].sort(
    (a, b) => a.position - b.position,
  );
  const songs: NewSectionSong[] = [...(data?.newSection?.songs || [])].sort(
    (a, b) => a.position - b.position,
  );

  // ── Featured item handlers ─────────────────────────────────────────────────
  const handleAddItem = async (form: ItemFormState): Promise<void> => {
    const result = await createItem({
      variables: {
        label: form.label.trim() || form.title.trim(),
        title: form.title.trim(),
        subtitle: form.subtitle.trim() || null,
        description: form.description.trim() || null,
        customImageUrl: form.customImageUrl.trim() || null,
        albumId: form.linkedAlbum?.id || null,
        position: items.length,
      },
    });
    if (result.errors?.length) {
      throw new Error(result.errors[0].message);
    }
    await refetch();
  };

  const handleSaveEdit = async (form: ItemFormState): Promise<void> => {
    if (!editItem) return;
    const result = await updateItem({
      variables: {
        _id: editItem._id,
        label: form.label.trim() || form.title.trim(),
        title: form.title.trim(),
        subtitle: form.subtitle.trim() || null,
        description: form.description.trim() || null,
        customImageUrl: form.customImageUrl.trim() || null,
        albumId: form.linkedAlbum?.id || editItem.album?._id || null,
      },
    });
    if (result.errors?.length) {
      throw new Error(result.errors[0].message);
    }
    await refetch();
  };

  const handleToggleActive = async (item: NewSectionItem) => {
    await updateItem({ variables: { _id: item._id, isActive: !item.isActive } });
    refetch();
  };

  const handleDeleteItem = async (id: string) => {
    await deleteItem({ variables: { _id: id } });
    refetch();
  };

  const handleMoveItem = async (index: number, direction: 'up' | 'down') => {
    const reordered = [...items];
    const swap = direction === 'up' ? index - 1 : index + 1;
    [reordered[index], reordered[swap]] = [reordered[swap], reordered[index]];
    await reorderItems({ variables: { orderedIds: reordered.map((i) => i._id) } });
    refetch();
  };

  // ── Song handlers ──────────────────────────────────────────────────────────
  const handleAddSong = async (songId: string) => {
    await createSong({ variables: { songId, position: songs.length } });
    refetch();
  };

  const handleDeleteSong = async (id: string) => {
    await deleteSong({ variables: { _id: id } });
    refetch();
  };

  const handleMoveSong = async (index: number, direction: 'up' | 'down') => {
    const reordered = [...songs];
    const swap = direction === 'up' ? index - 1 : index + 1;
    [reordered[index], reordered[swap]] = [reordered[swap], reordered[index]];
    await reorderSongs({ variables: { orderedIds: reordered.map((s) => s._id) } });
    refetch();
  };

  // Build initial values for edit dialog from an existing item
  const editInitialValues = editItem
    ? {
        label: editItem.label || '',
        title: editItem.title || '',
        subtitle: editItem.subtitle || '',
        description: editItem.description || '',
        customImageUrl: editItem.customImageUrl || '',
        linkedAlbum: editItem.album
          ? { id: editItem.album._id, name: editItem.album.title, image: editItem.album.imageUrl }
          : null,
      }
    : undefined;

  return (
    <Box className={classes.root}>
      <Typography variant="h4" className={classes.pageTitle}>Home Heroes</Typography>
      <Typography className={classes.pageSubtitle}>
        Manage the featured cards and best new songs shown in the "New" section on the user dashboard.
      </Typography>

      {/* ── Featured Items ─────────────────────────────────────────────────── */}
      <Box className={classes.section}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography className={classes.sectionTitle}>
            <LibraryMusicIcon className={classes.sectionIcon} /> Featured Items
          </Typography>
          <Button startIcon={<AddIcon />} className={classes.addButton} onClick={() => setAddItemOpen(true)}>
            Add Item
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} style={{ color: '#5dc4b2' }} />
          </Box>
        ) : (
          <TableContainer component={Paper} style={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell style={{ width: 40 }}>Order</TableCell>
                  <TableCell style={{ width: 72 }}>Art</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Subtitle</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell style={{ width: 80 }}>Active</TableCell>
                  <TableCell style={{ width: 80 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className={classes.emptyRow}>
                      No featured items yet — click "Add Item" to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, idx) => (
                    <TableRow key={item._id} className={classes.tableRow}>
                      <TableCell>
                        <Box display="flex" flexDirection="column">
                          <IconButton size="small" className={classes.orderBtn} disabled={idx === 0}
                            onClick={() => handleMoveItem(idx, 'up')}>
                            <ArrowUpIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" className={classes.orderBtn} disabled={idx === items.length - 1}
                            onClick={() => handleMoveItem(idx, 'down')}>
                            <ArrowDownIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <img
                          src={item.customImageUrl || item.album?.imageUrl || PLACEHOLDER}
                          alt={item.title}
                          className={classes.thumbnail}
                          onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER; }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 500 }}>
                          {item.title || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                          {item.subtitle || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.description ? (
                          <Tooltip title={item.description} placement="top">
                            <Typography className={classes.descPreview}>{item.description}</Typography>
                          </Tooltip>
                        ) : (
                          <Typography style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.85rem' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={item.label} size="small" className={classes.chip} />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={item.isActive}
                          onChange={() => handleToggleActive(item)}
                          size="small"
                          style={{ color: item.isActive ? '#5dc4b2' : undefined }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex">
                          <Tooltip title="Edit">
                            <IconButton size="small" className={classes.editBtn}
                              onClick={() => setEditItem(item)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton size="small" className={classes.deleteBtn}
                              onClick={() => handleDeleteItem(item._id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ── Best New Songs ─────────────────────────────────────────────────── */}
      <Box className={classes.section}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography className={classes.sectionTitle}>
            <MusicNoteIcon className={classes.sectionIcon} /> Best New Songs
          </Typography>
          <Button startIcon={<AddIcon />} className={classes.addButton} onClick={() => setAddSongOpen(true)}>
            Add Song
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} style={{ color: '#5dc4b2' }} />
          </Box>
        ) : (
          <TableContainer component={Paper} style={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell style={{ width: 40 }}>Order</TableCell>
                  <TableCell style={{ width: 56 }}>Art</TableCell>
                  <TableCell>Song</TableCell>
                  <TableCell>Artist</TableCell>
                  <TableCell style={{ width: 60 }}>Remove</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {songs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className={classes.emptyRow}>
                      No songs yet — click "Add Song" to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  songs.map((entry, idx) => (
                    <TableRow key={entry._id} className={classes.tableRow}>
                      <TableCell>
                        <Box display="flex" flexDirection="column">
                          <IconButton size="small" className={classes.orderBtn} disabled={idx === 0}
                            onClick={() => handleMoveSong(idx, 'up')}>
                            <ArrowUpIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" className={classes.orderBtn} disabled={idx === songs.length - 1}
                            onClick={() => handleMoveSong(idx, 'down')}>
                            <ArrowDownIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <img
                          src={entry.song?.image || PLACEHOLDER}
                          alt={entry.song?.name || 'Song'}
                          style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover', backgroundColor: '#02172a' }}
                          onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER; }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 500 }}>
                          {entry.song?.name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                          {entry.song?.primaryArtist?.displayName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Remove">
                          <IconButton size="small" className={classes.deleteBtn}
                            onClick={() => handleDeleteSong(entry._id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ── Dialogs ───────────────────────────────────────────────────────── */}
      <ItemDialog
        open={addItemOpen}
        dialogTitle="Add Featured Item"
        confirmLabel="Add to Home Heroes"
        onClose={() => setAddItemOpen(false)}
        onSave={handleAddItem}
      />

      <ItemDialog
        open={!!editItem}
        initialValues={editInitialValues}
        dialogTitle="Edit Featured Item"
        confirmLabel="Save Changes"
        onClose={() => setEditItem(null)}
        onSave={handleSaveEdit}
      />

      <AddSongDialog
        open={addSongOpen}
        onClose={() => setAddSongOpen(false)}
        onAdd={handleAddSong}
      />
    </Box>
  );
};

export default HomeHeroes;
